import React from 'react';
import type { TargetDatePresetId } from '../countdown/types';
import { TARGET_DATE_PRESETS } from '../countdown/types';

export const CountdownControls: React.FC<{
    activeModeLabel: string;
    onCycleMode: () => void;
    activeTargetLabel: string;
    targetPresetId: TargetDatePresetId;
    onSelectTargetPreset: (id: TargetDatePresetId) => void;
}> = ({
    activeModeLabel,
    onCycleMode,
    activeTargetLabel,
    targetPresetId,
    onSelectTargetPreset,
}) => {
    const [isMenuOpen, setIsMenuOpen] = React.useState<boolean>(false);
    const controlsRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (!isMenuOpen) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsMenuOpen(false);
        };

        const onMouseDown = (event: MouseEvent) => {
            const node = controlsRef.current;
            if (!node) return;
            const target = event.target as Node | null;
            if (target && node.contains(target)) return;
            setIsMenuOpen(false);
        };

        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('mousedown', onMouseDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('mousedown', onMouseDown);
        };
    }, [isMenuOpen]);

    return (
        <div className="countdownControls" ref={controlsRef}>
            <button
                type="button"
                className="countdownModeToggle"
                aria-label={`Přepnout režim odpočtu (aktuálně: ${activeModeLabel})`}
                title={`Režim: ${activeModeLabel}`}
                onClick={onCycleMode}
            >
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <polyline
                        points="17 1 21 5 17 9"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M3 11V9a4 4 0 0 1 4-4h14"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <polyline
                        points="7 23 3 19 7 15"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M21 13v2a4 4 0 0 1-4 4H3"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            <div className="countdownGearWrap">
                <button
                    type="button"
                    className="countdownGear"
                    aria-label="Nastavení cílového data"
                    title={`Cílové datum: ${activeTargetLabel}`}
                    aria-haspopup="menu"
                    aria-expanded={isMenuOpen ? 'true' : 'false'}
                    onClick={() => setIsMenuOpen((open) => !open)}
                >
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <circle
                            cx="12"
                            cy="12"
                            r="3"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.15.47.15.97 0 1.44a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1Z"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

                {isMenuOpen && (
                    <div className="countdownMenu" role="menu" aria-label="Cílové datum">
                        <div className="countdownMenuTitle" role="presentation">Cílové datum</div>
                        {TARGET_DATE_PRESETS.map((preset) => (
                            <button
                                key={preset.id}
                                type="button"
                                className="countdownMenuItem"
                                role="menuitemradio"
                                aria-checked={preset.id === targetPresetId ? 'true' : 'false'}
                                onClick={() => {
                                    onSelectTargetPreset(preset.id);
                                    setIsMenuOpen(false);
                                }}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
